import React, { useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ComposableMap, Geographies, Geography, Annotation } from 'react-simple-maps';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone,
  ArrowLeft,
  ArrowRight,
  Mic,
  MessageCircle,
  MapPin,
  X,
  Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { stateData, getState } from '@/data/stateData';

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// State name to abbreviation mapping
const stateNameToAbbr = {
  "Alabama": "AL", "Alaska": "AK", "Arizona": "AZ", "Arkansas": "AR", "California": "CA",
  "Colorado": "CO", "Connecticut": "CT", "Delaware": "DE", "Florida": "FL", "Georgia": "GA",
  "Hawaii": "HI", "Idaho": "ID", "Illinois": "IL", "Indiana": "IN", "Iowa": "IA",
  "Kansas": "KS", "Kentucky": "KY", "Louisiana": "LA", "Maine": "ME", "Maryland": "MD",
  "Massachusetts": "MA", "Michigan": "MI", "Minnesota": "MN", "Mississippi": "MS", "Missouri": "MO",
  "Montana": "MT", "Nebraska": "NE", "Nevada": "NV", "New Hampshire": "NH", "New Jersey": "NJ",
  "New Mexico": "NM", "New York": "NY", "North Carolina": "NC", "North Dakota": "ND", "Ohio": "OH",
  "Oklahoma": "OK", "Oregon": "OR", "Pennsylvania": "PA", "Rhode Island": "RI", "South Carolina": "SC",
  "South Dakota": "SD", "Tennessee": "TN", "Texas": "TX", "Utah": "UT", "Vermont": "VT",
  "Virginia": "VA", "Washington": "WA", "West Virginia": "WV", "Wisconsin": "WI", "Wyoming": "WY"
};

// State label positions for annotation
const stateLabels = {
  "AK": { x: -150, y: 63, dx: 0, dy: 0 },
  "HI": { x: -157, y: 20, dx: 0, dy: 0 }
};

// Small northeastern states that need easier clicking
const smallStates = ["VT", "NH", "ME", "MA", "RI", "CT", "NJ", "DE", "MD", "DC"];

const MapChart = memo(({ onStateClick, onStateHover, hoveredState }) => {
  return (
    <ComposableMap
      projection="geoAlbersUsa"
      projectionConfig={{ scale: 1000 }}
      className="w-full h-full"
    >
      <Geographies geography={geoUrl}>
        {({ geographies }) =>
          geographies.map((geo) => {
            const stateName = geo.properties.name;
            const stateAbbr = stateNameToAbbr[stateName];
            const isHovered = hoveredState === stateAbbr;
            const isAlaska = stateAbbr === "AK";
            const isSmallState = smallStates.includes(stateAbbr);
            
            return (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                onClick={() => onStateClick(stateAbbr)}
                onMouseEnter={() => onStateHover(stateAbbr)}
                onMouseLeave={() => onStateHover(null)}
                style={{
                  default: {
                    fill: isAlaska ? '#0ea5e9' : '#334155',
                    stroke: isSmallState ? '#475569' : '#1e293b',
                    strokeWidth: isSmallState ? 1.5 : 0.5,
                    outline: 'none',
                    cursor: 'pointer',
                    transition: 'fill 0.2s'
                  },
                  hover: {
                    fill: isAlaska ? '#38bdf8' : '#0ea5e9',
                    stroke: '#f8fafc',
                    strokeWidth: isSmallState ? 2 : 1,
                    outline: 'none',
                    cursor: 'pointer'
                  },
                  pressed: {
                    fill: '#0284c7',
                    stroke: '#1e293b',
                    strokeWidth: isSmallState ? 2 : 0.5,
                    outline: 'none'
                  }
                }}
              />
            );
          })
        }
      </Geographies>
    </ComposableMap>
  );
});

export default function InteractiveMapPage() {
  const navigate = useNavigate();
  const [hoveredState, setHoveredState] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const hoveredStateData = hoveredState ? getState(hoveredState) : null;
  const selectedStateData = selectedState ? getState(selectedState) : null;

  const handleStateClick = (stateAbbr) => {
    setSelectedState(stateAbbr);
    setShowModal(true);
  };

  const handleStateHover = (stateAbbr) => {
    setHoveredState(stateAbbr);
  };

  const navigateToDemo = (type) => {
    if (selectedState) {
      navigate(`/${type}-demo/hvac?state=${selectedState}`);
    }
    setShowModal(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/')}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-white" />
                </div>
                <div>
                  <span className="font-bold text-lg">Alaska AI Services</span>
                  <span className="hidden sm:inline text-slate-400 text-sm ml-2">| Nationwide Demo</span>
                </div>
              </div>
            </div>
            <Button
              onClick={() => window.open("https://calendly.com/jondcurtis", "_blank")}
              className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-semibold"
              data-testid="book-demo-cta"
            >
              Book a Demo
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-500/10 border border-sky-500/20 mb-6">
            <MapPin className="w-4 h-4 text-sky-400" />
            <span className="text-sm text-sky-400 font-medium">Serving All 50 States</span>
          </div>
          
          <h1 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
              Forged in Alaska.
            </span>{" "}
            Serving All 50.
          </h1>
          
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-2">
            Click any state to experience our AI assistant customized for your local market.
          </p>
          <p className="text-sm text-slate-500">
            Our AI understands local weather patterns, seasonal demands, and regional business needs.
          </p>
        </motion.div>

        {/* State Info Tooltip */}
        <AnimatePresence>
          {hoveredStateData && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="fixed top-32 left-1/2 -translate-x-1/2 z-30 bg-slate-800 border border-slate-700 rounded-xl px-6 py-4 shadow-xl max-w-md"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center font-bold">
                  {hoveredStateData.abbr}
                </div>
                <div>
                  <h3 className="font-bold text-white">{hoveredStateData.name}</h3>
                  <p className="text-sm text-slate-400">{hoveredStateData.timezone} Time</p>
                </div>
              </div>
              <p className="text-sm text-slate-300">{hoveredStateData.seasonalNotes}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Map Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 sm:p-6 mb-8"
        >
          <div className="aspect-[16/10] sm:aspect-[16/9] lg:aspect-[2/1]">
            <MapChart
              onStateClick={handleStateClick}
              onStateHover={handleStateHover}
              hoveredState={hoveredState}
            />
          </div>
          
          {/* Legend and State Selector */}
          <div className="mt-4 pt-4 border-t border-slate-800">
            <div className="flex flex-wrap items-center justify-center gap-6 mb-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-sky-500" />
                <span className="text-sm text-slate-400">Alaska (HQ)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-slate-600" />
                <span className="text-sm text-slate-400">All States Served</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-sky-400" />
                <span className="text-sm text-slate-400">Hover to Preview</span>
              </div>
            </div>
            
            {/* State Dropdown for Small States */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4 border-t border-slate-800/50">
              <span className="text-sm text-slate-500">Hard to click small states?</span>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    handleStateClick(e.target.value);
                    e.target.value = '';
                  }
                }}
                className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white cursor-pointer hover:border-sky-500 transition-colors text-sm"
                data-testid="state-dropdown"
              >
                <option value="">Select a state...</option>
                {Object.entries(stateNameToAbbr).sort((a, b) => a[0].localeCompare(b[0])).map(([name, abbr]) => (
                  <option key={abbr} value={abbr}>{name}</option>
                ))}
              </select>
            </div>
          </div>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
          >
            <div className="w-12 h-12 rounded-xl bg-sky-500/10 flex items-center justify-center mb-4">
              <MapPin className="w-6 h-6 text-sky-400" />
            </div>
            <h3 className="font-bold text-lg mb-2">Local Knowledge</h3>
            <p className="text-slate-400 text-sm">
              Our AI understands each state's climate, seasonal patterns, and common service needs.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
          >
            <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center mb-4">
              <Mic className="w-6 h-6 text-green-400" />
            </div>
            <h3 className="font-bold text-lg mb-2">Voice & Chat</h3>
            <p className="text-slate-400 text-sm">
              Try both AI voice calls and text chat demos customized for your state.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
          >
            <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4">
              <Phone className="w-6 h-6 text-purple-400" />
            </div>
            <h3 className="font-bold text-lg mb-2">24/7 Coverage</h3>
            <p className="text-slate-400 text-sm">
              Same great service across all time zones. Never miss a lead again.
            </p>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <p className="text-slate-400 mb-4">Ready to see it in action for your business?</p>
          <Button
            onClick={() => window.open("https://calendly.com/jondcurtis", "_blank")}
            size="lg"
            className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-bold px-8"
            data-testid="bottom-cta"
          >
            Book Your Free Demo
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </motion.div>
      </main>

      {/* State Selection Modal */}
      <AnimatePresence>
        {showModal && selectedStateData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-lg w-full"
              onClick={(e) => e.stopPropagation()}
              data-testid="state-modal"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center font-bold text-xl">
                    {selectedStateData.abbr}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">{selectedStateData.name}</h2>
                    <p className="text-sm text-slate-400">Try our AI demo for this state</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                  data-testid="close-modal"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              {/* State Info */}
              <div className="bg-slate-800/50 rounded-xl p-4 mb-6">
                <p className="text-sm text-slate-300 mb-3">{selectedStateData.seasonalNotes}</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Major Cities:</span>
                    <p className="text-slate-300">{selectedStateData.majorCities.slice(0, 3).join(", ")}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Timezone:</span>
                    <p className="text-slate-300">{selectedStateData.timezone}</p>
                  </div>
                </div>
              </div>

              {/* Demo Options */}
              <div className="space-y-3">
                <button
                  onClick={() => navigateToDemo('voice')}
                  className="w-full flex items-center gap-4 p-4 bg-slate-800 hover:bg-slate-700 rounded-xl transition-colors"
                  data-testid="voice-demo-btn"
                >
                  <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                    <Mic className="w-6 h-6 text-green-400" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-white">AI Voice Demo</div>
                    <div className="text-sm text-slate-400">Simulate a phone call with our AI</div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-500 ml-auto" />
                </button>

                <button
                  onClick={() => navigateToDemo('chat')}
                  className="w-full flex items-center gap-4 p-4 bg-slate-800 hover:bg-slate-700 rounded-xl transition-colors"
                  data-testid="chat-demo-btn"
                >
                  <div className="w-12 h-12 rounded-xl bg-sky-500/20 flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-sky-400" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-white">AI Chat Demo</div>
                    <div className="text-sm text-slate-400">Text conversation with our AI</div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-500 ml-auto" />
                </button>

                <div className="border-t border-slate-700 pt-3 mt-3">
                  <button
                    onClick={() => {
                      setShowModal(false);
                      navigate(`/contractors/${selectedState}`);
                    }}
                    className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 hover:from-purple-500/20 hover:to-pink-500/20 border border-purple-500/20 rounded-xl transition-colors"
                    data-testid="find-contractors-btn"
                  >
                    <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-purple-400" />
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-white">Find Local Contractors</div>
                      <div className="text-sm text-slate-400">Browse verified providers in {selectedStateData.name}</div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-500 ml-auto" />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-6 h-6 rounded bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
              <Phone className="w-3 h-3 text-white" />
            </div>
            <span className="font-bold">Alaska AI Services</span>
          </div>
          <p className="text-sm text-slate-500">
            Forged in Alaska. Serving All 50. © 2026 Alaska AI Services.
          </p>
        </div>
      </footer>
    </div>
  );
}
